import { useState, useEffect, useRef } from "react";

// Custom Hooks
import useUserToken from "@/hook/useUserToken";
import { useChatSocket } from "@/components/chat/ChatSocketProvider";

// Third Party
import { io } from "socket.io-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

// UI
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import { FileUploadPopover } from "@/components/chat/FileUploadPopover";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import RenderMessage from "@/components/chat/RenderMessage";

// Icons
import {
	FileBarChart,
	FilePlus2,
	Image,
	MapPin,
	Paperclip,
	Send,
	FileText
} from "lucide-react";

// Services and Types
import {
	createConversation,
	getMessage,
	sendMessage,
	updateMessageRead,
} from "@/services/chat";
import { CHAT_AVATAR_SMALL } from "@/components/constants";
import type { Message, UserChat } from "@/types/chat";
import calculatePassedTime from "@/utils/calculatePassedTime";
import { uploadFile } from "@/helpers/presign-url";

// Sample chat data

export const Route = createFileRoute("/_layout-private/chat/_layout/$id/")({
	component: RouteComponent,
	validateSearch: (search) => {
		return {
			profileUrl: search.profileUrl as string | undefined,
			fullName: search.fullName as string | undefined,
			phone: search.phone as string | undefined,
			receiverId: search.receiverId as string | undefined,
		};
	},
});

function RouteComponent() {
	const { id } = Route.useParams();
	const search = Route.useSearch();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { toast } = useToast();
	// const socket = io(CHAT_ENDPOINT);
	const { socket, emitTyping, emitReadMessage, emitNewMessage } = useChatSocket();

	// State
	const user = useUserToken()?.data;
	const [messages, setMessages] = useState([] as Message[]);
	const [newMessage, setNewMessage] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileData, setSelectedFileData] = useState<{
		messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE" | null;
		fileName?: string;
		fileUrl?: string;
		isClickReceive?: boolean;
	}>({
		messageType: null,
		fileName: undefined,
		fileUrl: undefined
	});
	const [typingUsers, setTypingUsers] = useState<string[]>([]);
	const [isSending, setIsSending] = useState(false);
	const [sendingContent, setSendingContent] = useState<{
		text: string;
		fileData: {
			messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE" | null;
			fileName?: string;
		} | null;
	}>({ text: "", fileData: null });

	// States
	const [popoverOpen, setPopoverOpen] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView();
	};

	console.log(id, "Conversation ID");

	// Query
	const { data: messagesData, isLoading } = useQuery({
		queryKey: ["messages", id],
		queryFn: () =>
			getMessage(id, {
				limit: 100,
				skip: 0,
			}),
		enabled: id !== "TEMP",
	});

	const messagesList = messagesData?.data as Message[];

	// Mutation
	const createConversationMutation = useMutation({
		mutationFn: createConversation,
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["messages", id],
			});
			queryClient.invalidateQueries({
				queryKey: ["conversationList"],
			});

			console.log("Conversation created:", data);

			const conversation = data?.data?.conversation;

			console.log(conversation, "Conversation");

			navigate({
				to: "/chat/$id",
				params: {
					id: conversation?._id,
				},
				search: {
					...search,
				},
				replace: true,
			});

			// scrollToBottom();

			setNewMessage("");
		},
		onError: (error) => {
			console.error("Error creating conversation:", error);

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});



	const handleFileSelect = async ({
		file,
		messageType,
		sendImmediately = false,
		isClickReceive,
	}: {
		file: File;
		type: "image" | "document" | null;
		messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE";
		fileName?: string;
		fileUrl?: string;
		sendImmediately?: boolean;
		isClickReceive?: boolean;
	}) => {
		try {
			scrollToBottom();

			console.log("Starting file upload process for:", file.name);
			setFileUploading(true);

			// Upload the file
			const uploadResult = await uploadFile(file);
			console.log("Upload completed:", uploadResult);

			// Store the file data
			const fileData = {
				messageType,
				fileName: uploadResult.fileName,
				fileUrl: uploadResult.fileUrl,
				// Only include isClickReceive for document files (FILE type)
				isClickReceive: messageType === "FILE" ? isClickReceive : undefined,
			};

			setSelectedFileData(fileData);

			// If sendImmediately is true, send the message right away
			if (sendImmediately) {
				// Send via socket for group conversations
				setSendingContent({
					text: "",
					fileData: {
						messageType,
						fileName: uploadResult.fileName
					}
				});
				setIsSending(true);

				const messageData = {
					conversationId: id,
					messageType: messageType,
					content: messageType,
					senderId: user?.userChatId,
					receiverId: search?.receiverId,
					attachments: [
						{
							fileName: uploadResult.fileName,
							fileUrl: uploadResult.fileUrl,
						},
					],
					// Only include isClickReceive for document files (FILE type)
					...(messageType === "FILE" && { isClickReceive: isClickReceive }),
				};

				console.log("Sending group message with attachment:", messageData);

				const sent = emitNewMessage("PRIVATE_NEW_MESSAGE", messageData);

				// Clear selected file data since we've sent it
				setSelectedFileData({
					messageType: null,
					fileName: undefined,
					fileUrl: undefined,
				});

				setTimeout(() => {
					setIsSending(false);
					setSendingContent({ text: "", fileData: null });
				}, 1500);
			} else {
				toast({
					title: "ສຳເລັດ",
					description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ, ກົດປຸ່ມສົ່ງເພື່ອສົ່ງຂໍ້ຄວາມ",
				});
			}
		} catch (error) {
			// ... error handling code remains the same
		} finally {
			setFileUploading(false);
		}
	};

	// Add typing handler
	const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewMessage(e.target.value);

		if (id !== "TEMP") {
			emitTyping(id, e.target.value.length > 0);
		}
	};

	// Effects
	useEffect(() => {
		scrollToBottom();
	}, [messagesList]);

	// Mark messages as read
	useEffect(() => {
		if (id !== "TEMP") {
			emitReadMessage(id);
		}
	}, [messagesList]);

	// Listen for typing events
	useEffect(() => {
		if (!socket || id === "TEMP") return;

		socket.on("TYPING", ({ userId, isTyping }) => {
			if (userId === search?.receiverId) {
				setTypingUsers(isTyping ? [search?.fullName || "User"] : []);
			}
		});

		return () => {
			socket.off("TYPING");
		};
	}, [socket, search?.receiverId, search?.fullName, id]);

	// Functions


	const handleSendMessage = () => {
		scrollToBottom();

		setSendingContent({
			text: newMessage,
			fileData: selectedFileData?.messageType ? {
				messageType: selectedFileData.messageType,
				fileName: selectedFileData.fileName
			} : null
		});

		setIsSending(true);

		// For new conversations (TEMP id)
		try {
			if (id === "TEMP") {
				// If there's a file to send with a new conversation
				if (selectedFileData?.fileUrl || selectedFileData?.fileName) {
					const attachments = [
						{
							fileName: selectedFileData.fileName,
							fileUrl: selectedFileData.fileUrl,
						},
					];

					createConversationMutation.mutate({
						senderId: user?.userChatId,
						receiverId: search?.receiverId,
						content: selectedFileData.messageType || "",
						messageType: selectedFileData.messageType || "FILE",
						attachments: attachments,
						...(selectedFileData.messageType === "FILE" && {
							isClickReceive: selectedFileData.isClickReceive,
						}),
					});

					// Clear file data
					setSelectedFileData({
						messageType: null,
						fileName: undefined,
						fileUrl: undefined,
					});
					return;
				}

				// For text messages in new conversations
				if (!newMessage.trim()) return;

				createConversationMutation.mutate({
					senderId: user?.userChatId,
					receiverId: search?.receiverId,
					content: newMessage,
					messageType: "TEXT",
				});

				setNewMessage("");
				return;
			}

			// For existing conversations - use socket

			// If there's a file to send
			if (selectedFileData?.fileUrl || selectedFileData?.fileName) {
				setFileUploading(true);

				console.log("Sending file message with data:", selectedFileData);

				// Prepare message data
				const messageData = {
					conversationId: id,
					messageType: selectedFileData.messageType,
					content: selectedFileData.messageType,
					senderId: user?.userChatId,
					receiverId: search?.receiverId,
					attachments: [
						{
							fileName: selectedFileData.fileName,
							fileUrl: selectedFileData.fileUrl,
						},
					],
					// Only include isClickReceive for document files (FILE type)
					...(selectedFileData.messageType === "FILE" && {
						isClickReceive: selectedFileData.isClickReceive,
					}),
				};

				console.log("Payload Data", messageData);

				// Send via socket for private conversations
				const sent = emitNewMessage("PRIVATE_NEW_MESSAGE", messageData);

				if (!sent) {
					toast({
						title: "ຫຼົ້ມເຫຼວ",
						description: "ບໍ່ສາມາດສົ່ງຂໍ້ຄວາມໄດ້, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
					});
				}

				// Clear file data
				setSelectedFileData({
					messageType: null,
					fileName: undefined,
					fileUrl: undefined,
				});
				setFileUploading(false);
				return;
			}

			// For text messages in existing conversations
			if (!newMessage.trim()) return;

			// Send via socket for private conversations
			const messageData = {
				conversationId: id,
				messageType: "TEXT",
				content: newMessage,
				senderId: user?.userChatId,
				receiverId: search?.receiverId,
			};

			const sent = emitNewMessage("PRIVATE_NEW_MESSAGE", messageData);

			if (!sent) {
				toast({
					title: "ຫຼົ້ມເຫຼວ",
					description: "ບໍ່ສາມາດສົ່ງຂໍ້ຄວາມໄດ້, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				});
			}
		} finally {
			setTimeout(() => {
				setNewMessage("");
				setIsSending(false);
				// Reset sending content after the timeout
				setSendingContent({ text: "", fileData: null });
			}, 1500);
			setSelectedFileData({
				messageType: null,
				fileName: undefined,
				fileUrl: undefined,
			});
			setFileUploading(false);
		}


	};

	return (
		<div className="flex flex-col w-full overflow-hidden h-[calc(100vh-200px)]">
			<header className="flex relative justify-between items-center p-4 border-b">
				<div className="flex gap-2 items-center">
					<Avatar className="w-[40px] h-[40px]">
						<AvatarImage src={CHAT_AVATAR_SMALL + search?.profileUrl} />
						<AvatarFallback>{search?.fullName?.charAt(0)}</AvatarFallback>
					</Avatar>
					<div className="text-sm">
						<h2 className="font-bold text-[18px]">{search?.fullName}</h2>
						<small className="text-muted-foreground">{search?.phone}</small>
					</div>
				</div>

				{/* <Button
					size={"icon"}
					variant={"ghost"}
					onClick={() =>
						navigate({
							to: "/chat/$id/document",
							params: {
								id: id,
							},
						})
					}
				>
					<FileBarChart />
				</Button> */}
			</header>

			<section className="flex-1 w-full overflow-y-auto p-[24px] space-y-4 relative">
				{isLoading ? (
					<MessageLoadingComponent />
				) : (
					<>
						{[...(messagesList || [])]?.reverse()?.map((msg) => (
							<div
								key={msg._id}
								className={cn(
									"flex gap-3 items-start",
									msg?.sender?._id === user?.userChatId
										? "flex-row-reverse"
										: "flex-row",
								)}
							>
								<Avatar>
									<AvatarImage
										className="w-[40px] h-[40px] object-cover"
										src={CHAT_AVATAR_SMALL + (msg?.sender?._id === user?.userChatId ? user?.profileUrl : search?.profileUrl)}
									/>
									<AvatarFallback>
										{msg?.sender?._id === user?.userChatId
											? user?.firstName?.charAt(0)
											: search?.fullName?.charAt(0)}
									</AvatarFallback>
								</Avatar>

								<div className={cn("max-w-[70%]")}>
									<RenderMessage

										message={msg}
										content={msg?.content}
										type={msg?.messageType || "TEXT"}
										isCurrentUser={msg?.sender?._id === user?.userChatId}
										senderName={msg?.sender?._id === user?.userChatId ? `${user?.firstName} ${user?.lastName}` : search?.fullName}
										showSender={false} // No need to show sender in private chat
										timestamp={calculatePassedTime(msg?.sendAt)}
										attachments={msg?.attachments}
										location={msg?.location}
									/>
								</div>
							</div>
						))}
						{typingUsers.length > 0 && (
							<div className="text-sm italic text-muted-foreground">
								{search?.fullName} is typing...
							</div>
						)}
						{isSending && (
							<div className={cn(
								"flex flex-row-reverse gap-3 items-start"
							)}>
								<Avatar>
									<AvatarImage
										className="w-[40px] h-[40px] object-cover"
										src={CHAT_AVATAR_SMALL + user?.profileUrl}
									/>
									<AvatarFallback>
										{user?.firstName?.charAt(0)}
									</AvatarFallback>
								</Avatar>
								<div className={cn("max-w-[55%]")}>
									<div className="p-3 rounded-lg bg-primary text-primary-foreground">
										{sendingContent.fileData ? (
											<>
												{sendingContent.fileData.messageType === "IMAGE" ? (
													<div className="flex flex-col gap-2">
														<div className="flex gap-2 items-center">
															<FileText className="w-4 h-4" />
															<p className="text-sm font-medium">ຮູບພາບ</p>
														</div>
														<div className="flex justify-center items-center w-full h-32 rounded bg-muted/20">
															<Spinner className="w-6 h-6" />
														</div>
													</div>
												) : sendingContent.fileData.messageType === "VIDEO" ? (
													<div className="flex flex-col gap-2">
														<div className="flex gap-2 items-center">
															<FileText className="w-4 h-4" />
															<p className="text-sm font-medium">ວິດີໂອ</p>
														</div>
														<div className="flex justify-center items-center w-full h-32 rounded bg-muted/20">
															<Spinner className="w-6 h-6" />
														</div>
													</div>
												) : (
													<div className="flex gap-2 items-center">
														<FileText className="w-4 h-4" />
														<p className="text-sm font-medium truncate">
															{sendingContent.fileData.fileName || "ເອກະສານ"}
														</p>
													</div>
												)}
											</>
										) : (
											<p className="text-sm">{sendingContent.text}</p>
										)}
										<div className="flex gap-1 items-center mt-2">
											<Spinner className="w-3 h-3" />
											<p className="text-xs opacity-70">ກຳລັງສົ່ງ...</p>
										</div>
									</div>
								</div>
							</div>
						)}
						<div ref={messagesEndRef} />
					</>
				)}
			</section>

			<footer className="h-[64px] py-2 px-4 flex items-center gap-2 border-t">
				<FileUploadPopover
					initialFile={selectedFile}
					onFileSelect={handleFileSelect}
					isOpen={popoverOpen}
					setIsOpen={setPopoverOpen}
				/>

				<input
					type="text"
					value={newMessage}
					onChange={handleTyping}
					onKeyPress={(e) => {
						if (e.key === "Enter") handleSendMessage();
					}}
					placeholder="ພິມຂໍ້ຄວາມ..."
					className="p-2 w-full border-b outline-none border-primary"
				/>

				<Button size={"icon"} variant={"ghost"} onClick={handleSendMessage}>
					{/* {createConversationMutation?.isPending ||
						sendMessageMutation.isPending ? (
						<Spinner />
					) : (
						<Send />
					)} */}
					<Send />
				</Button>
			</footer>
		</div>
	);
}

export const MessageLoadingComponent = () => {
	return (
		<div className="flex flex-col gap-4 p-4 w-full">
			{/* Left message */}
			<div className="flex gap-2 items-start">
				<Skeleton className="w-8 h-8 rounded-full" />
				<div className="flex flex-col gap-2">
					<Skeleton className="h-4 w-[250px]" />
					<Card className="p-4">
						<Skeleton className="h-4 w-[200px] mb-2" />
						<Skeleton className="h-4 w-[150px]" />
					</Card>
				</div>
			</div>

			{/* Right message */}
			<div className="flex gap-2 justify-end items-start">
				<div className="flex flex-col gap-2 items-end">
					<Skeleton className="h-4 w-[250px]" />
					<Card className="p-4">
						<Skeleton className="h-4 w-[180px] mb-2" />
						<Skeleton className="h-4 w-[120px]" />
					</Card>
				</div>
				<Skeleton className="w-8 h-8 rounded-full" />
			</div>

			{/* Left message */}
			<div className="flex gap-2 items-start">
				<Skeleton className="w-8 h-8 rounded-full" />
				<div className="flex flex-col gap-2">
					<Skeleton className="h-4 w-[200px]" />
					<Card className="p-4">
						<Skeleton className="h-4 w-[160px]" />
					</Card>
				</div>
			</div>
		</div>
	);
};
